import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LogIn, Clock, Monitor, Users, Activity, ChevronDown, ChevronRight,
  Search, RefreshCw, Trash2, AlertCircle,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useUserStore } from '@/store/userStore';

const API = process.env.REACT_APP_BACKEND_URL;

const MODULE_LABELS = {
  dashboard: 'Dashboard', ops: 'OPS', liderazgo: 'Liderazgo',
  'near-misses': 'Near Misses', siniestralidad: 'Siniestralidad',
  correlacion: 'Correlación', accidentes: 'Accidentes', mapa: 'Mapa',
  admin: 'Administración', ayuda: 'Ayuda',
};

const ROLE_COLOR = {
  superadmin: '#00C853', admin: '#4A9EFF', ops: '#FFB900', viewer: '#aaa',
};

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-xl p-4 flex items-center gap-3"
      style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)' }}>
      <div className="rounded-lg p-2" style={{ background: `${color}18` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <div>
        <p className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>{label}</p>
        <p className="text-xl font-bold text-white">{value}</p>
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg px-3 py-2 text-xs space-y-1"
      style={{ background: 'var(--vz-bg-card)', border: '1px solid var(--vz-border-subtle)' }}>
      <p className="font-semibold text-white mb-1">{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
};

function ActivityChart({ token, chartDays }) {
  const { data: activity = [], isLoading } = useQuery({
    queryKey: ['access-activity', chartDays],
    queryFn: async () => {
      const r = await fetch(`${API}/api/admin/access-log/activity?days=${chartDays}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return [];
      return r.json();
    },
    staleTime: 60 * 1000,
  });

  if (isLoading) return <Skeleton className="h-48 w-full rounded-xl" style={{ background: 'var(--vz-bg-elevated)' }} />;
  const hasData = activity.some(d => d.logins > 0);

  return (
    <div className="rounded-xl p-4" style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)' }}>
      <p className="text-xs font-semibold text-white mb-3">Actividad diaria — últimos {chartDays} días</p>
      {!hasData ? (
        <div className="h-40 flex items-center justify-center" style={{ color: 'var(--vz-text-muted)' }}>
          <p className="text-xs">Sin actividad registrada en este período</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={activity} margin={{ top: 4, right: 4, left: -20, bottom: 0 }} barSize={14}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis dataKey="day_label" tick={{ fontSize: 10, fill: 'var(--vz-text-muted)' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10, fill: 'var(--vz-text-muted)' }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
            <Legend wrapperStyle={{ fontSize: 10, color: 'var(--vz-text-muted)', paddingTop: 8 }} />
            <Bar dataKey="unique_users" name="Usuarios únicos" fill="#4A9EFF" radius={[3, 3, 0, 0]} />
            <Bar dataKey="logins" name="Total logins" fill="#00C85366" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function SessionRow({ session }) {
  const [expanded, setExpanded] = useState(false);
  const dur = session.session_duration_min || 0;
  const durLabel = dur === 0 ? '< 1 min' : dur < 60 ? `${dur} min` : `${Math.floor(dur / 60)}h ${dur % 60}m`;
  const modules = (session.modules_visited || []).filter(Boolean);
  const loginDate = session.login_at ? new Date(session.login_at).toLocaleString('es-ES') : '—';
  const lastDate = session.last_seen ? new Date(session.last_seen).toLocaleString('es-ES') : '—';
  const loginType = session.login_type === 'msal' ? 'Microsoft SSO' : 'Contraseña';

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--vz-border-subtle)' }}>
      <div
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-white/5 transition-colors"
        style={{ background: 'var(--vz-bg-elevated)' }}
        onClick={() => setExpanded(v => !v)}
        data-testid={`session-row-${session.session_id}`}
      >
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
          style={{ background: `${ROLE_COLOR[session.role] || '#aaa'}22`, color: ROLE_COLOR[session.role] || '#aaa' }}>
          {(session.display_name || session.email || '?')[0].toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-white truncate">{session.display_name || session.email}</p>
            <Badge className="text-[10px] flex-shrink-0"
              style={{ background: `${ROLE_COLOR[session.role] || '#aaa'}15`, color: ROLE_COLOR[session.role] || '#aaa', border: `1px solid ${ROLE_COLOR[session.role] || '#aaa'}33` }}>
              {session.role}
            </Badge>
            <span className="text-[10px] flex-shrink-0 px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--vz-text-muted)' }}>
              {loginType}
            </span>
          </div>
          <p className="text-xs truncate" style={{ color: 'var(--vz-text-secondary)' }}>{session.email}</p>
        </div>
        <div className="text-right flex-shrink-0 hidden sm:block">
          <p className="text-xs font-mono" style={{ color: 'var(--vz-text-secondary)' }}>{loginDate}</p>
          <p className="text-[10px] flex items-center gap-1 justify-end" style={{ color: 'var(--vz-text-muted)' }}>
            <Clock size={9} /> {durLabel}
          </p>
        </div>
        <div className="flex-shrink-0 ml-1">
          {expanded ? <ChevronDown size={14} style={{ color: 'var(--vz-text-muted)' }} /> : <ChevronRight size={14} style={{ color: 'var(--vz-text-muted)' }} />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-3 pt-1 space-y-2" style={{ background: 'rgba(0,0,0,0.15)' }}>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <p style={{ color: 'var(--vz-text-muted)' }}>Último acceso</p>
              <p className="text-white font-mono text-[11px]">{lastDate}</p>
            </div>
            <div>
              <p style={{ color: 'var(--vz-text-muted)' }}>Duración sesión</p>
              <p className="text-white font-medium">{durLabel}</p>
            </div>
          </div>
          {modules.length > 0 && (
            <div>
              <p className="text-xs mb-1.5" style={{ color: 'var(--vz-text-muted)' }}>Módulos visitados</p>
              <div className="flex flex-wrap gap-1">
                {modules.map(m => (
                  <span key={m} className="text-[10px] px-2 py-0.5 rounded-full"
                    style={{ background: 'rgba(74,158,255,0.12)', color: '#4A9EFF', border: '1px solid rgba(74,158,255,0.2)' }}>
                    {MODULE_LABELS[m] || m}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function AccesosTab() {
  const { token } = useUserStore();
  const qc = useQueryClient();
  const [emailFilter, setEmailFilter] = useState('');
  const [days, setDays] = useState(30);
  const [chartDays, setChartDays] = useState(14);
  const [page, setPage] = useState(1);
  const [cleanupConfirm, setCleanupConfirm] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin-access-log', emailFilter, days, page],
    queryFn: async () => {
      const params = new URLSearchParams({ page, limit: 50, days });
      if (emailFilter) params.append('email', emailFilter);
      const r = await fetch(`${API}/api/admin/access-log?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Error cargando accesos');
      return r.json();
    },
    staleTime: 30 * 1000,
  });

  const cleanupMut = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${API}/api/admin/cleanup-test-data`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Error en limpieza');
      return r.json();
    },
    onSuccess: (res) => {
      toast.success(`Limpieza completada: ${res.deleted_total} registros eliminados`);
      setCleanupConfirm(false);
      qc.invalidateQueries(['admin-access-log']);
    },
    onError: () => toast.error('Error al limpiar datos de test'),
  });

  const sessions = data?.data || [];
  const stats = data?.stats || {};
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / 50);

  return (
    <div className="space-y-5" data-testid="accesos-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm font-semibold text-white">Historial de Accesos</p>
          <p className="text-xs mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
            Sesiones de login, duración y módulos visitados por usuario
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => refetch()} className="text-xs gap-1.5 h-7" style={{ color: 'var(--vz-text-secondary)' }}>
            <RefreshCw size={11} /> Actualizar
          </Button>
          {!cleanupConfirm ? (
            <Button
              variant="outline" size="sm" onClick={() => setCleanupConfirm(true)}
              className="text-xs gap-1.5 h-7"
              style={{ borderColor: 'rgba(229,57,53,0.4)', color: '#e53935', background: 'transparent' }}
              data-testid="cleanup-btn"
            >
              <Trash2 size={11} /> Limpiar datos test
            </Button>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-xs" style={{ color: '#e53935' }}>¿Confirmar?</span>
              <Button size="sm" onClick={() => cleanupMut.mutate()} disabled={cleanupMut.isPending}
                className="text-xs h-7 px-3" style={{ background: '#e53935', color: '#fff' }}
                data-testid="cleanup-confirm-btn">
                {cleanupMut.isPending ? 'Limpiando...' : 'Sí, eliminar'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setCleanupConfirm(false)} className="text-xs h-7 px-2">
                No
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Activity Chart */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium" style={{ color: 'var(--vz-text-secondary)' }}>Gráfico de actividad</p>
          <div className="flex gap-1">
            {[7, 14, 30].map(d => (
              <button key={d} onClick={() => setChartDays(d)}
                className="text-[10px] px-2 py-0.5 rounded-full transition-all"
                style={{
                  background: chartDays === d ? 'rgba(74,158,255,0.15)' : 'rgba(255,255,255,0.04)',
                  color: chartDays === d ? '#4A9EFF' : 'var(--vz-text-muted)',
                  border: `1px solid ${chartDays === d ? 'rgba(74,158,255,0.3)' : 'rgba(255,255,255,0.08)'}`,
                }}
              >{d}d</button>
            ))}
          </div>
        </div>
        <ActivityChart token={token} chartDays={chartDays} />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={LogIn} label="Logins hoy" value={stats.today_logins ?? '—'} color="#00C853" />
        <StatCard icon={Users} label="Usuarios únicos hoy" value={stats.unique_users_today ?? '—'} color="#4A9EFF" />
        <StatCard icon={Activity} label="Total sesiones" value={total} color="#FFB900" />
        <StatCard icon={Monitor} label="Últimos días" value={days} color="#9b59b6" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-40">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--vz-text-muted)' }} />
          <Input
            value={emailFilter}
            onChange={e => { setEmailFilter(e.target.value); setPage(1); }}
            placeholder="Filtrar por email..."
            className="pl-7 h-8 text-xs"
            style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)', color: 'white' }}
            data-testid="access-log-email-filter"
          />
        </div>
        <div className="flex items-center gap-1">
          {[7, 15, 30, 90].map(d => (
            <button key={d} onClick={() => { setDays(d); setPage(1); }}
              className="text-xs px-2.5 py-1 rounded-full transition-all"
              style={{
                background: days === d ? 'rgba(74,158,255,0.15)' : 'rgba(255,255,255,0.04)',
                color: days === d ? '#4A9EFF' : 'var(--vz-text-secondary)',
                border: `1px solid ${days === d ? 'rgba(74,158,255,0.3)' : 'rgba(255,255,255,0.08)'}`,
              }}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Sessions list */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-14 w-full rounded-xl" style={{ background: 'var(--vz-bg-elevated)' }} />)}
        </div>
      ) : sessions.length === 0 ? (
        <div className="text-center py-12" style={{ color: 'var(--vz-text-muted)' }}>
          <AlertCircle size={36} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No hay registros de acceso en los últimos {days} días</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sessions.map((s, i) => <SessionRow key={s.session_id || i} session={s} />)}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>
            Página {page} de {totalPages} · {total} registros
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="text-xs h-7">
              Anterior
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="text-xs h-7">
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
